// Ability templates referenced by `ability_id`. Each defines a special attack
// a unit can fire when off cooldown. Damage is added on top of normal attacks.
import type { Unit } from './unit';

export interface AbilityDef {
  id: string;
  name: string;
  cooldown: number;     // seconds
  range: number;        // world units
  cast: (caster: Unit, world: { units: Unit[] }) => void;
  fx: 'fire' | 'ice' | 'electric' | 'grass' | 'shadow' | 'physical' | 'heal' | 'aoe';
}

function nearestEnemy(u: Unit, units: Unit[], maxRange = Infinity): Unit | null {
  let best: Unit | null = null;
  let bestD = Infinity;
  for (const o of units) {
    if (o.dead || o.side === u.side) continue;
    const d = u.distanceTo(o);
    if (d < bestD && d <= maxRange) { best = o; bestD = d; }
  }
  return best;
}

function enemiesInRadius(u: Unit, units: Unit[], radius: number): Unit[] {
  return units.filter(o => !o.dead && o.side !== u.side && u.distanceTo(o) <= radius);
}

function alliesInRadius(u: Unit, units: Unit[], radius: number): Unit[] {
  return units.filter(o => !o.dead && o.side === u.side && o !== u && u.distanceTo(o) <= radius);
}

export const ABILITIES: Record<string, AbilityDef> = {
  // ----- Pokémon -----
  flamethrower: {
    id: 'flamethrower', name: 'Flamethrower', cooldown: 5, range: 14, fx: 'fire',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.4, 'fire');
    },
  },
  hydro_pump: {
    id: 'hydro_pump', name: 'Hydro Pump', cooldown: 5, range: 14, fx: 'fire',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.4, 'ice');
    },
  },
  thunderbolt: {
    id: 'thunderbolt', name: 'Thunderbolt', cooldown: 4, range: 14, fx: 'electric',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      // chain to up to 3 enemies
      const hit = new Set<Unit>([t]);
      let cur: Unit | null = t;
      const dmg = u.character.attack * 0.9;
      while (hit.size < 3 && cur) {
        cur.takeDamage(dmg, 'electric');
        let next: Unit | null = null; let nd = 6;
        for (const o of w.units) {
          if (o.dead || o.side === u.side || hit.has(o)) continue;
          const d = cur.distanceTo(o);
          if (d < nd) { nd = d; next = o; }
        }
        cur = next;
        if (cur) hit.add(cur);
      }
    },
  },
  solar_beam: {
    id: 'solar_beam', name: 'Solar Beam', cooldown: 6, range: 16, fx: 'grass',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 16);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.6, 'grass');
    },
  },
  ice_beam: {
    id: 'ice_beam', name: 'Ice Beam', cooldown: 5, range: 14, fx: 'ice',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.2, 'ice');
      t.applyStatus('slow', 3);
    },
  },
  shadow_ball: {
    id: 'shadow_ball', name: 'Shadow Ball', cooldown: 5, range: 12, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 12);
      if (!t) return;
      // ignores defense
      u.shootProjectile(t, u.character.attack * 1.3, 'shadow', true);
    },
  },
  psybeam: {
    id: 'psybeam', name: 'Psybeam', cooldown: 4, range: 13, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 13);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.2, 'shadow');
    },
  },
  dragon_claw: {
    id: 'dragon_claw', name: 'Dragon Claw', cooldown: 4, range: 4, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 4);
      if (!t) return;
      t.takeDamage(u.character.attack * 1.5, 'physical', false, u.pos);
    },
  },
  body_slam: {
    id: 'body_slam', name: 'Body Slam', cooldown: 5, range: 4, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 4);
      if (!t) return;
      t.takeDamage(u.character.attack * 1.4, 'physical', false, u.pos);
      t.applyStatus('stun', 1);
    },
  },
  tackle: {
    id: 'tackle', name: 'Tackle', cooldown: 3, range: 3, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 3);
      if (!t) return;
      t.takeDamage(u.character.attack * 1.1, 'physical', false, u.pos);
    },
  },

  // ----- Plants -----
  pea_volley: {
    id: 'pea_volley', name: 'Pea Volley', cooldown: 3, range: 14, fx: 'grass',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.0, 'grass');
    },
  },
  double_pea: {
    id: 'double_pea', name: 'Double Pea', cooldown: 3, range: 14, fx: 'grass',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 0.8, 'grass');
      setTimeout(() => !t.dead && u.shootProjectile(t, u.character.attack * 0.8, 'grass'), 200);
    },
  },
  triple_pea: {
    id: 'triple_pea', name: 'Triple Pea', cooldown: 4, range: 14, fx: 'grass',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 3);
      enemies.forEach(t => u.shootProjectile(t, u.character.attack * 0.7, 'grass'));
    },
  },
  frost_pea: {
    id: 'frost_pea', name: 'Frost Pea', cooldown: 3, range: 14, fx: 'ice',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.0, 'ice');
      t.applyStatus('slow', 2);
    },
  },
  lob: {
    id: 'lob', name: 'Cabbage Lob', cooldown: 3, range: 14, fx: 'grass',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.1, 'grass');
    },
  },
  lob_aoe: {
    id: 'lob_aoe', name: 'Melon Splash', cooldown: 4, range: 14, fx: 'aoe',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.2, 'grass');
      // splash to nearby
      setTimeout(() => {
        if (!t || t.dead) return;
        enemiesInRadius(t, w.units, 3).forEach(e => e.takeDamage(u.character.attack * 0.5, 'physical'));
      }, 400);
    },
  },
  butter_stun: {
    id: 'butter_stun', name: 'Butter', cooldown: 4, range: 13, fx: 'grass',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 13);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 0.9, 'grass');
      if (Math.random() < 0.4) t.applyStatus('stun', 2);
    },
  },
  heal_aura: {
    id: 'heal_aura', name: 'Sun Aura', cooldown: 4, range: 8, fx: 'heal',
    cast: (u, w) => {
      alliesInRadius(u, w.units, 8).forEach(a => a.heal(15));
      u.heal(8);
      u.spawnEffect('heal');
    },
  },
  taunt: {
    id: 'taunt', name: 'Taunt', cooldown: 6, range: 0, fx: 'physical',
    cast: (u) => { u.applyStatus('taunt', 4); },
  },
  suicide_blast: {
    id: 'suicide_blast', name: 'Cherry Bomb', cooldown: 999, range: 5, fx: 'aoe',
    cast: (u, w) => {
      enemiesInRadius(u, w.units, 5).forEach(e => e.takeDamage(u.character.attack * 2, 'fire'));
      u.spawnEffect('explosion');
      u.takeDamage(99999, 'true');
    },
  },
  fire_line: {
    id: 'fire_line', name: 'Jalapeño Fire', cooldown: 999, range: 30, fx: 'fire',
    cast: (u, w) => {
      w.units.filter(o => !o.dead && o.side !== u.side).forEach(e => e.takeDamage(u.character.attack * 1.5, 'fire'));
      u.spawnEffect('explosion');
      u.takeDamage(99999, 'true');
    },
  },
  crush: {
    id: 'crush', name: 'Squash Crush', cooldown: 999, range: 4, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 4);
      if (t) t.takeDamage(t.maxHp * 0.5, 'physical');
      u.takeDamage(99999, 'true');
    },
  },
  fire_buff: {
    id: 'fire_buff', name: 'Torch Buff', cooldown: 8, range: 6, fx: 'fire',
    cast: (u, w) => { alliesInRadius(u, w.units, 6).forEach(a => a.applyStatus('buffed', 5)); },
  },
  instakill_bite: {
    id: 'instakill_bite', name: 'Devour', cooldown: 12, range: 3, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 3);
      if (t) t.takeDamage(t.hp * 0.4, 'physical');
    },
  },

  // ----- Zombies -----
  bite: {
    id: 'bite', name: 'Bite', cooldown: 3, range: 2, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 2);
      if (t) t.takeDamage(u.character.attack * 1.1, 'physical', false, u.pos);
    },
  },
  smash: {
    id: 'smash', name: 'Smash', cooldown: 5, range: 4, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 4);
      if (!t) return;
      t.takeDamage(u.character.attack * 1.5, 'physical', false, u.pos);
      enemiesInRadius(t, w.units, 2).forEach(e => e.takeDamage(u.character.attack * 0.6, 'physical'));
    },
  },
  rally: {
    id: 'rally', name: 'Rally', cooldown: 8, range: 8, fx: 'physical',
    cast: (u, w) => { alliesInRadius(u, w.units, 8).forEach(a => a.applyStatus('haste', 4)); },
  },
  leap: {
    id: 'leap', name: 'Leap', cooldown: 5, range: 8, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.teleportNear(t);
      t.takeDamage(u.character.attack * 1.2, 'physical', false, u.pos);
    },
  },
  rage: {
    id: 'rage', name: 'Rage', cooldown: 10, range: 0, fx: 'physical',
    cast: (u) => { u.applyStatus('haste', 6); u.applyStatus('buffed', 6); },
  },
  summon: {
    id: 'summon', name: 'Summon', cooldown: 12, range: 0, fx: 'shadow',
    cast: (u) => { u.applyStatus('haste', 4); },
  },
  fly: {
    id: 'fly', name: 'Float', cooldown: 8, range: 0, fx: 'physical',
    cast: (u) => { u.applyStatus('haste', 4); },
  },
  tunnel: {
    id: 'tunnel', name: 'Tunnel', cooldown: 6, range: 14, fx: 'physical',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side);
      const back = enemies.sort((a, b) => (u.side === 'left' ? b.pos.x - a.pos.x : a.pos.x - b.pos.x))[0];
      if (back) { u.teleportNear(back); back.takeDamage(u.character.attack * 1.3, 'physical', false, u.pos); }
    },
  },
};

// ===== Signature spells (per famous character) =====

const SIGNATURE: Record<string, AbilityDef> = {
  volt_tackle: {
    id: 'volt_tackle', name: 'Volt Tackle', cooldown: 5, range: 14, fx: 'electric',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.teleportNear(t);
      u.spawnEffect('lightning');
      enemiesInRadius(t, w.units, 3.5).forEach(e => e.takeDamage(u.character.attack * 1.3, 'electric', false, u.pos));
    },
  },
  blast_burn: {
    id: 'blast_burn', name: 'Blast Burn', cooldown: 6, range: 14, fx: 'fire',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 5);
      enemies.forEach(t => u.shootProjectile(t, u.character.attack * 1.0, 'fire'));
      u.spawnEffect('fire');
    },
  },
  hydro_cannon: {
    id: 'hydro_cannon', name: 'Hydro Cannon', cooldown: 6, range: 14, fx: 'ice',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.8, 'ice');
      u.spawnEffect('water');
    },
  },
  frenzy_plant: {
    id: 'frenzy_plant', name: 'Frenzy Plant', cooldown: 6, range: 14, fx: 'grass',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 4);
      enemies.forEach(t => {
        t.takeDamage(u.character.attack * 1.1, 'grass', false, u.pos);
        t.applyStatus('slow', 2);
        u.spawnEffect('grass');
      });
    },
  },
  psystrike: {
    id: 'psystrike', name: 'Psystrike', cooldown: 5, range: 16, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 16);
      if (!t) return;
      t.takeDamage(u.character.attack * 1.6, 'shadow', true, u.pos);
      u.spawnEffect('psychic');
    },
  },
  aeroblast: {
    id: 'aeroblast', name: 'Aeroblast', cooldown: 6, range: 16, fx: 'ice',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 16);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.5, 'ice');
      enemiesInRadius(t, w.units, 3).forEach(e => e.takeDamage(u.character.attack * 0.5, 'ice'));
      u.spawnEffect('water');
    },
  },
  sacred_fire: {
    id: 'sacred_fire', name: 'Sacred Fire', cooldown: 6, range: 14, fx: 'fire',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 3);
      enemies.forEach(t => {
        u.shootProjectile(t, u.character.attack * 1.3, 'fire');
        t.applyStatus('burning', 3);
      });
      u.spawnEffect('fire');
    },
  },
  dragon_pulse: {
    id: 'dragon_pulse', name: 'Dragon Pulse', cooldown: 5, range: 14, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.5, 'shadow');
      u.spawnEffect('dragon');
    },
  },
  stone_edge: {
    id: 'stone_edge', name: 'Stone Edge', cooldown: 5, range: 12, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 12);
      if (!t) return;
      t.takeDamage(u.character.attack * 1.6, 'physical', false, u.pos);
      enemiesInRadius(t, w.units, 2.5).forEach(e => e.takeDamage(u.character.attack * 0.4, 'physical'));
      u.spawnEffect('rock');
    },
  },
  aura_sphere: {
    id: 'aura_sphere', name: 'Aura Sphere', cooldown: 4, range: 14, fx: 'ice',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.3, 'ice', true);
      u.spawnEffect('water');
    },
  },
  earth_power: {
    id: 'earth_power', name: 'Earth Power', cooldown: 5, range: 6, fx: 'physical',
    cast: (u, w) => {
      enemiesInRadius(u, w.units, 6).forEach(e => {
        e.takeDamage(u.character.attack * 1.2, 'physical', false, u.pos);
        e.applyStatus('slow', 2);
      });
      u.spawnEffect('rock');
    },
  },
  moonblast: {
    id: 'moonblast', name: 'Moonblast', cooldown: 5, range: 14, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.4, 'shadow');
      u.spawnEffect('moon');
    },
  },
  shadow_force: {
    id: 'shadow_force', name: 'Shadow Force', cooldown: 6, range: 16, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 16);
      if (!t) return;
      u.teleportNear(t);
      u.spawnEffect('dark');
      t.takeDamage(u.character.attack * 1.8, 'shadow', true, u.pos);
    },
  },
  fire_blast: {
    id: 'fire_blast', name: 'Fire Blast', cooldown: 5, range: 14, fx: 'fire',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.4, 'fire');
      // splash burning to nearby
      enemiesInRadius(t, w.units, 3).forEach(e => e.applyStatus('burning', 3));
      u.spawnEffect('fire');
    },
  },
  leaf_storm: {
    id: 'leaf_storm', name: 'Leaf Storm', cooldown: 5, range: 14, fx: 'grass',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 4);
      enemies.forEach(t => u.shootProjectile(t, u.character.attack * 0.9, 'grass'));
      u.spawnEffect('grass');
    },
  },
  water_pulse: {
    id: 'water_pulse', name: 'Water Pulse', cooldown: 4, range: 14, fx: 'ice',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.2, 'ice');
      if (Math.random() < 0.4) t.applyStatus('stun', 1);
      u.spawnEffect('water');
    },
  },
  flash_cannon: {
    id: 'flash_cannon', name: 'Flash Cannon', cooldown: 5, range: 16, fx: 'electric',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 16);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.5, 'electric', true);
      u.spawnEffect('sparkle');
    },
  },
  night_slash: {
    id: 'night_slash', name: 'Night Slash', cooldown: 4, range: 3, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 3);
      if (!t) return;
      // double-strike
      t.takeDamage(u.character.attack * 1.0, 'shadow', false, u.pos);
      t.takeDamage(u.character.attack * 1.0, 'shadow', false, u.pos);
      u.spawnEffect('dark');
    },
  },
  outrage: {
    id: 'outrage', name: 'Outrage', cooldown: 6, range: 14, fx: 'shadow',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 4);
      enemies.forEach(t => u.shootProjectile(t, u.character.attack * 1.0, 'shadow'));
      u.applyStatus('haste', 4);
      u.spawnEffect('dragon');
    },
  },
  bug_buzz: {
    id: 'bug_buzz', name: 'Bug Buzz', cooldown: 5, range: 6, fx: 'grass',
    cast: (u, w) => {
      enemiesInRadius(u, w.units, 6).forEach(e => {
        e.takeDamage(u.character.attack * 1.2, 'grass', true, u.pos);
        e.applyStatus('slow', 2);
      });
      u.spawnEffect('bug');
    },
  },
  roar_of_time: {
    id: 'roar_of_time', name: 'Roar of Time', cooldown: 8, range: 16, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 16);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 2.2, 'shadow', true);
      u.spawnEffect('time');
    },
  },
  spacial_rend: {
    id: 'spacial_rend', name: 'Spacial Rend', cooldown: 7, range: 16, fx: 'shadow',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 16);
      if (!t) return;
      u.shootProjectile(t, u.character.attack * 1.8, 'shadow', true);
      enemiesInRadius(t, w.units, 3).forEach(e => e.applyStatus('stun', 1.5));
      u.spawnEffect('space');
    },
  },
  judgment: {
    id: 'judgment', name: 'Judgment', cooldown: 7, range: 16, fx: 'electric',
    cast: (u, w) => {
      const types = ['fire','ice','electric','grass','shadow','physical'];
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 6);
      enemies.forEach((t, i) => u.shootProjectile(t, u.character.attack * 1.0, types[i % types.length]));
      u.spawnEffect('sparkle');
    },
  },
  behemoth_blade: {
    id: 'behemoth_blade', name: 'Behemoth Blade', cooldown: 5, range: 4, fx: 'physical',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 4);
      if (!t) return;
      t.takeDamage(u.character.attack * 2.0, 'physical', true, u.pos);
      u.spawnEffect('sword');
    },
  },
  glacial_lance: {
    id: 'glacial_lance', name: 'Glacial Lance', cooldown: 6, range: 14, fx: 'ice',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 3);
      enemies.forEach(t => {
        t.takeDamage(u.character.attack * 1.3, 'ice', false, u.pos);
        t.applyStatus('frozen', 2);
        t.applyStatus('stun', 2);
      });
      u.spawnEffect('ice');
    },
  },
  water_shuriken: {
    id: 'water_shuriken', name: 'Water Shuriken', cooldown: 3, range: 14, fx: 'ice',
    cast: (u, w) => {
      const t = nearestEnemy(u, w.units, 14);
      if (!t) return;
      for (let i = 0; i < 4; i++) setTimeout(() => !t.dead && u.shootProjectile(t, u.character.attack * 0.6, 'ice'), i * 90);
    },
  },
  dragon_darts: {
    id: 'dragon_darts', name: 'Dragon Darts', cooldown: 4, range: 14, fx: 'shadow',
    cast: (u, w) => {
      const enemies = w.units.filter(o => !o.dead && o.side !== u.side).slice(0, 2);
      enemies.forEach(t => {
        u.shootProjectile(t, u.character.attack * 0.9, 'shadow');
        u.shootProjectile(t, u.character.attack * 0.9, 'shadow');
      });
    },
  },
  disarming_voice: {
    id: 'disarming_voice', name: 'Disarming Voice', cooldown: 4, range: 8, fx: 'shadow',
    cast: (u, w) => {
      enemiesInRadius(u, w.units, 8).forEach(e => e.takeDamage(u.character.attack * 1.0, 'shadow', true, u.pos));
      u.spawnEffect('heart');
    },
  },
};

// Merge into ABILITIES so they're discoverable too.
Object.assign(ABILITIES, SIGNATURE);

// Per-character signature-spell overrides. Keys match `character.id`.
const CHARACTER_OVERRIDES: Record<string, string> = {
  // Pokémon (id format: poke-<lowercase>)
  'poke-pikachu':       'volt_tackle',
  'poke-charizard':     'blast_burn',
  'poke-blastoise':     'hydro_cannon',
  'poke-venusaur':      'frenzy_plant',
  'poke-mewtwo':        'psystrike',
  'poke-mew':           'psystrike',
  'poke-lugia':         'aeroblast',
  'poke-ho-oh':         'sacred_fire',
  'poke-rayquaza':      'dragon_pulse',
  'poke-dialga':        'roar_of_time',
  'poke-palkia':        'spacial_rend',
  'poke-arceus':        'judgment',
  'poke-snorlax':       'body_slam',
  'poke-gengar':        'shadow_force',
  'poke-dragonite':     'dragon_pulse',
  'poke-tyranitar':     'stone_edge',
  'poke-gyarados':      'hydro_cannon',
  'poke-alakazam':      'psystrike',
  'poke-machamp':       'stone_edge',
  'poke-golem':         'stone_edge',
  'poke-lapras':        'aeroblast',
  'poke-jolteon':       'volt_tackle',
  'poke-vaporeon':      'water_pulse',
  'poke-flareon':       'fire_blast',
  'poke-umbreon':       'night_slash',
  'poke-espeon':        'psystrike',
  'poke-sylveon':       'moonblast',
  'poke-lucario':       'aura_sphere',
  'poke-garchomp':      'earth_power',
  'poke-metagross':     'flash_cannon',
  'poke-salamence':     'outrage',
  'poke-greninja':      'water_shuriken',
  'poke-infernape':     'fire_blast',
  'poke-torterra':      'frenzy_plant',
  'poke-empoleon':      'flash_cannon',
  'poke-swampert':      'water_pulse',
  'poke-sceptile':      'leaf_storm',
  'poke-blaziken':      'fire_blast',
  'poke-scizor':        'bug_buzz',
  'poke-gardevoir':     'moonblast',
  'poke-absol':         'night_slash',
  'poke-aggron':        'stone_edge',
  'poke-flygon':        'earth_power',
  'poke-milotic':       'water_pulse',
  'poke-rotom':         'volt_tackle',
  'poke-zoroark':       'night_slash',
  'poke-hydreigon':     'outrage',
  'poke-volcarona':     'bug_buzz',
  'poke-mimikyu':       'shadow_force',
  'poke-toxapex':       'bug_buzz',
  'poke-dragapult':     'dragon_darts',
  'poke-urshifu':       'night_slash',
  'poke-zacian':        'behemoth_blade',
  'poke-zamazenta':     'behemoth_blade',
  'poke-eternatus':     'dragon_pulse',
  'poke-calyrex':       'glacial_lance',
  'poke-koraidon':      'outrage',
  'poke-miraidon':      'flash_cannon',
  'poke-meowscarada':   'leaf_storm',
  'poke-quaquaval':     'water_shuriken',
  'poke-skeledirge':    'sacred_fire',
};

export function getAbility(id: string): AbilityDef {
  return ABILITIES[id] ?? ABILITIES.tackle;
}

export function getAbilityForCharacter(c: { id: string; ability_id: string }): AbilityDef {
  const override = CHARACTER_OVERRIDES[c.id];
  if (override && ABILITIES[override]) return ABILITIES[override];
  return getAbility(c.ability_id);
}
